import React, { useState } from "react";
import { Link2, FormInput, Map, ArrowLeft, Check, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { AddAddressMode, AddPatientAddressPayload, PatientAddress } from "./types";
import GoogleLinkAddressForm from "./GoogleLinkAddressForm";
import ManualAddressForm from "./ManualAddressForm";
import MapLocationPicker, { GeocodedLocationData } from "./MapLocationPicker";
import { AddressService } from "./address.service";

interface AddAddressWizardProps {
  patientId: string;
  onAddressCreated: (address: PatientAddress) => void;
  onCancel: () => void;
}

export const AddAddressWizard: React.FC<AddAddressWizardProps> = ({
  patientId,
  onAddressCreated,
  onCancel,
}) => {
  const [mode, setMode] = useState<AddAddressMode>("google_link");

  // State for Option 3 (Select on Map)
  const [mapLocation, setMapLocation] = useState<GeocodedLocationData | null>(null);
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [addressName, setAddressName] = useState("Flat");
  const [landmark, setLandmark] = useState("");
  const [savingMapAddress, setSavingMapAddress] = useState(false);

  const handleMapLocationSelect = (data: GeocodedLocationData) => {
    setMapLocation(data);
    if (data.address) setAddress(data.address);
    if (data.city) setCity(data.city);
    if (data.state) setState(data.state);
    if (data.pincode) setPincode(data.pincode);
  };

  const handleSaveMapAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mapLocation) {
      toast.error("Please click on map to select location");
      return;
    }
    if (!address.trim()) {
      toast.error("Address is required");
      return;
    }
    if (!pincode.trim()) {
      toast.error("Pincode is required");
      return;
    }

    const payload: AddPatientAddressPayload = {
      patientId,
      address: address.trim(),
      city: city.trim() || "Lucknow",
      state: state.trim() || "Uttar Pradesh",
      pincode: pincode.trim(),
      latitude: mapLocation.lat,
      longitude: mapLocation.lng,
      isDefault: false,
      addressName: addressName.trim() || "Flat",
      landmark: landmark.trim(),
    };

    try {
      setSavingMapAddress(true);
      const res = await AddressService.addPatientAddress(payload);
      if (res.success && res.data) {
        toast.success("Address saved successfully!");
        onAddressCreated(res.data);
      } else {
        const newAddress: PatientAddress = {
          id: res.data?.id || Date.now(),
          patientId,
          address: payload.address,
          city: payload.city,
          state: payload.state,
          pincode: payload.pincode,
          latitude: payload.latitude,
          longitude: payload.longitude,
          addressName: payload.addressName,
          landmark: payload.landmark,
        };
        toast.success(res.message || "Address saved!");
        onAddressCreated(newAddress);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save address");
    } finally {
      setSavingMapAddress(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Back to List */}
      <div className="flex items-center justify-between pb-2 border-b border-slate-100">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs font-bold text-slate-600 hover:text-slate-800 flex items-center gap-1.5 cursor-pointer"
        >
          <ArrowLeft size={14} /> Back to Address List
        </button>
        <span className="text-[0.7rem] font-bold text-emerald-600 uppercase tracking-wider bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
          Create New Address
        </span>
      </div>

      {/* Mode Selector Tabs */}
      <div className="grid grid-cols-3 gap-1.5 p-1 bg-slate-100/80 rounded-xl">
        <button
          type="button"
          onClick={() => setMode("google_link")}
          className={`py-2 px-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            mode === "google_link"
              ? "bg-white text-slate-800 shadow-xs"
              : "text-slate-500 hover:text-slate-700 bg-transparent"
          }`}
        >
          <Link2 size={13} className="text-emerald-600" />
          Google Link
        </button>
        <button
          type="button"
          onClick={() => setMode("manual")}
          className={`py-2 px-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            mode === "manual"
              ? "bg-white text-slate-800 shadow-xs"
              : "text-slate-500 hover:text-slate-700 bg-transparent"
          }`}
        >
          <FormInput size={13} className="text-blue-600" />
          Manual Form
        </button>
        <button
          type="button"
          onClick={() => setMode("map")}
          className={`py-2 px-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            mode === "map"
              ? "bg-white text-slate-800 shadow-xs"
              : "text-slate-500 hover:text-slate-700 bg-transparent"
          }`}
        >
          <Map size={13} className="text-purple-600" />
          Select on Map
        </button>
      </div>

      {/* Render Mode Component */}
      {mode === "google_link" && (
        <GoogleLinkAddressForm
          patientId={patientId}
          onAddressCreated={onAddressCreated}
          onCancel={onCancel}
        />
      )}

      {mode === "manual" && (
        <ManualAddressForm
          patientId={patientId}
          onAddressCreated={onAddressCreated}
          onCancel={onCancel}
        />
      )}

      {mode === "map" && (
        <form onSubmit={handleSaveMapAddress} className="flex flex-col gap-3.5">
          <div className="flex flex-col gap-1">
            <label className="text-[0.7rem] font-bold text-slate-500 uppercase tracking-wide">
              Click on Map to Select Location
            </label>
            <MapLocationPicker onLocationSelect={handleMapLocationSelect} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">
                Full Address <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Auto-filled from map or enter address"
                required
                className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800"
              />
            </div>

            <div>
              <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800"
              />
            </div>

            <div>
              <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">State</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="State"
                className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800"
              />
            </div>

            <div>
              <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">
                Pincode <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
                placeholder="6-digit Pincode"
                required
                className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800"
              />
            </div>

            <div>
              <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">Tag (Address Name)</label>
              <select
                value={addressName}
                onChange={(e) => setAddressName(e.target.value)}
                className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800 bg-white"
              >
                <option value="Home">Home</option>
                <option value="Work">Work</option>
                <option value="Flat">Flat / Apartment</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onCancel}
              disabled={savingMapAddress}
              className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={savingMapAddress || !address.trim() || !pincode.trim()}
              className="px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1.5 shadow-xs"
            >
              {savingMapAddress ? (
                <>
                  <Loader2 size={13} className="animate-spin" /> Saving Address...
                </>
              ) : (
                <>
                  <Check size={14} /> Save & Select Address
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddAddressWizard;
